import { motion } from "framer-motion";
import {
  ArrowRight,
  PhoneCall,
  ClipboardList,
  BellRing,
  Lightbulb,
  RefreshCcw,
} from "lucide-react";
import { siteContent } from "@/content/site";

const STEP_ICONS = {
  phone: PhoneCall,
  clipboard: ClipboardList,
  bell: BellRing,
  lightbulb: Lightbulb,
  refresh: RefreshCcw,
};

const { coordinator } = siteContent;

export default function Coordinator() {
  return (
    <section
      id="coordinator"
      data-testid="coordinator-section"
      className="relative py-20 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5 lg:sticky lg:top-28 self-start"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8A33D]">
            {coordinator.eyebrow}
          </span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0E2A36] leading-tight">
            {coordinator.headingLead}{" "}
            <em className="italic font-medium text-[#1F4E5F]">
              {coordinator.headingItalic}
            </em>{" "}
            {coordinator.headingTail}
          </h2>
          <p className="mt-5 text-[#3D5560] text-lg leading-relaxed">
            {coordinator.intro}
          </p>
          <p className="mt-4 text-[#3D5560] text-base leading-relaxed">
            {coordinator.intro2}
          </p>
          <button
            data-testid="coordinator-cta"
            onClick={() =>
              document.getElementById("beta")?.scrollIntoView({ behavior: "smooth" })
            }
            className="mt-7 inline-flex items-center gap-2 bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8] px-6 py-3 rounded-full text-sm font-semibold transition-all hover:gap-3"
          >
            {coordinator.cta} <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>

        <div className="lg:col-span-7 relative">
          <div className="absolute left-[26px] top-3 bottom-3 w-px bg-gradient-to-b from-[#E8A33D] via-[#1F4E5F] to-transparent opacity-40 hidden sm:block" />
          <div className="space-y-5">
            {coordinator.steps.map((s, i) => {
              const Icon = STEP_ICONS[s.iconKey] || PhoneCall;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.07 }}
                  className="relative flex gap-5 group"
                >
                  <div className="shrink-0 w-[54px] h-[54px] rounded-2xl bg-[#1F4E5F] text-[#F4FAF8] flex items-center justify-center font-display font-bold text-lg relative z-10 shadow-[0_8px_24px_rgba(15,56,32,0.18)] group-hover:bg-[#E8A33D] transition-colors">
                    <span className="absolute top-1.5 right-1.5 text-[10px] opacity-80">
                      0{i + 1}
                    </span>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 pt-1 pb-5 border-b border-[#C9DDD6] last:border-0">
                    <h3 className="font-display text-xl font-semibold text-[#0E2A36]">
                      {s.title}
                    </h3>
                    <p className="mt-1.5 text-[#3D5560] text-[15px] leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
